// Purpose: Provide console diagnostics and privacy-safe persistent application
// failure records for the LGV admin log viewer.
// Features: Serializes safe error details, redacts secrets and personal-data
// fields recursively, and writes actionable logger.error calls to Firestore.
// Dependencies: Firebase Auth and Firestore plus browser runtime configuration.
// Important behavior: warn/log remain console-only; persisted contexts never
// include credentials, contact details, auth identifiers, or raw stack traces.
// The access-controlled log envelope deliberately retains the authenticated UID
// for incident correlation; Firestore Rules bind it to the actual caller.
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";

// 🔧 Logging configuration
const isLocalhost =
  typeof window !== "undefined" &&
  window.location.hostname === "localhost";

const enableProdLogs =
  process.env.REACT_APP_ENABLE_PROD_LOGS === "true";

const shouldLog = isLocalhost || enableProdLogs;

const sanitizeLogString = (value) => String(value)
  .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[REDACTED_EMAIL]")
  .replace(/Bearer\s+[A-Z0-9._~+/-]+=*/gi, "Bearer [REDACTED]");

const getErrorDetails = (context) => {
  const candidate = context?.error || context?.value || context;

  if (!candidate || typeof candidate !== "object") {
    return { errorCode: null, errorName: null, errorMessage: null };
  }

  return {
    errorCode: typeof candidate.code === "string" ? candidate.code.slice(0, 200) : null,
    errorName: typeof candidate.name === "string" ? candidate.name.slice(0, 200) : null,
    errorMessage:
      typeof candidate.message === "string"
        ? sanitizeLogString(candidate.message).slice(0, 500)
        : null,
  };
};

const getLogEnvironment = () => {
  if (typeof window === "undefined") return "server";
  return window.location.hostname === "localhost" ? "local" : "hosted";
};

// 🔁 Safely serialize any value (avoid FirebaseError or circular refs)
export const serializeLogContext = (input) => {
  const seen = new WeakSet();
  const sensitiveKeyPattern = /(?:authorization|cookie|password|passcode|secret|token|oobcode|api.?key|credential|card|cvv|payment|email|phone|address|first.?name|last.?name|display.?name|user.?id|uid)/i;

  const visit = (value, key = "") => {
    if (sensitiveKeyPattern.test(key)) return "[REDACTED]";
    if (value == null) return value;

    if (value instanceof Error) {
      return {
        name: value.name || "Error",
        message: sanitizeLogString(value.message || ""),
        code: value.code || null,
      };
    }

    if (typeof value === "function") return undefined;
    if (typeof value === "string") return sanitizeLogString(value);
    if (typeof value !== "object") return value;
    if (value instanceof Date) return value.toISOString();

    if (seen.has(value)) return "[Circular]";
    seen.add(value);

    if (Array.isArray(value)) return value.map((item) => visit(item));

    return Object.fromEntries(
      Object.entries(value)
        .map(([nestedKey, nestedValue]) => [
          nestedKey,
          visit(nestedValue, nestedKey),
        ])
        .filter(([, nestedValue]) => nestedValue !== undefined)
    );
  };

  return visit(input);
};

// 🔎 Console log handler
const logToConsole = (level, message, context) => {
  const safeMessage = sanitizeLogString(message || "");
  const safeContext = serializeLogContext(context);

  if (console[level]) {
    console[level](`[${level.toUpperCase()}] ${safeMessage}`, safeContext || {});
  } else {
    console.log(`[${level.toUpperCase()}] ${safeMessage}`, safeContext || {});
  }
};

// 🔥 Firestore log writer
const logToFirestore = async (level, message, context) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    const safeContext = serializeLogContext(context);
    const logEntry = {
      level,
      message: sanitizeLogString(message || "Application failure").slice(0, 500),
      context: safeContext,
      uid: user?.uid || null,
      path: typeof window !== "undefined" ? window.location.pathname : null,
      environment: getLogEnvironment(),
      online: typeof navigator === "undefined" ? null : navigator.onLine,
      ...getErrorDetails(safeContext),
      timestamp: serverTimestamp(),
    };

    await addDoc(collection(db, "logs"), logEntry);
  } catch (e) {
    console.warn("⚠️ logger.js failed to log to Firestore:", e.message);
  }
};

// ✅ Unified logger
export const logger = {
  log: (message, context) => {
    if (shouldLog) logToConsole("log", message, context);
  },

  warn: (message, context) => {
    if (shouldLog) logToConsole("warn", message, context);
  },

  error: (message, context) => {
    logToConsole("error", message, context);
    logToFirestore("error", message, context);
  },
};
